import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { FormikTouched, setNestedObjectValues, useFormikContext } from 'formik'
import { isEmpty } from 'lodash'

import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { CancelIcon } from 'components/Icons/CancelIcon'
import Modal from 'components/v2/Modal'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ItinerariesCard } from 'features/Services/components/NewServicesV2/components/ItinerariesCard'
import { VehiclesCard } from 'features/Services/components/NewServicesV2/components/VehiclesCard'
import { IEditServiceForm } from 'features/Services/components/EditService/types'
import { IPlanningModalProps } from './types'
import { InputDate } from 'components/v2/InputDate'
import { IItinerary } from 'features/Services/components/NewServicesV2/components/ItinerariesCard/types'
import { getActivePeriod, getRemaingSecondsBeforeEditing, isEditPlanningCoolingDown } from 'features/Services/utils'
import { ItineraryNotificationContainer } from 'features/Services/components/NewServicesV2/components/ItineraryNotification'
import { secondsToMilliseconds } from 'date-fns'
import { pad2 } from 'utils/string'

const ModalContainer = styled(Modal.Container)`
  background-color: #fafaff;
  box-shadow: 8px 8px 16px rgba(102, 119, 150, 0.1);
  max-width: 1390px;
  width: calc(100% - 32px);

  & > div {
    padding: 0px;
  }
`

const PlanningModalCloseButton = styled.button<TSFixMe>`
  align-items: center;
  background-color: #e0d6ff;
  border: none;
  cursor: pointer;
  display: flex;
  font-weight: 500;
  justify-content: center;
  padding: 8px;
  gap: 8px;
  width: 100%;
`

const PlanningModalContent = styled(Modal.Content)`
  padding: 48px;
`

const messages = defineMessages({
  startDateLabel: {
    id: 'services.planning.startDateLabel',
    defaultMessage: 'Start date'
  },
  endDateLabel: {
    id: 'services.planning.endDateLabel',
    defaultMessage: 'End date'
  }
})

let interval: NodeJS.Timeout

export const PlanningModal = ({
  companyId,
  oldService,
  plannings,
  activePlanningIndex,
  currentServiceIndex = 0,
  onClose,
  isEndDateOptional
}: IPlanningModalProps): ReactElement | null => {
  const intl = useIntl()
  const activePeriod = useMemo(
    (): number =>
      getActivePeriod({
        activePeriod: oldService.activePeriod ? parseInt(oldService.activePeriod) : undefined,
        startDate: oldService.startDate as Date,
        endDate: oldService.endDate
      }),
    [oldService]
  )
  const { values, setFieldValue, errors, setTouched } = useFormikContext<IEditServiceForm>()
  const [timer, setTimer] = useState(getRemaingSecondsBeforeEditing(oldService.priceCheckedDate as Date))
  const [editPlanningDisabled, setEditPlanningDisabled] = useState(
    isEditPlanningCoolingDown(oldService.priceCheckedDate as Date, activePeriod)
  )

  useEffect(() => {
    if (isEditPlanningCoolingDown(oldService.priceCheckedDate as Date, activePeriod)) {
      interval = setInterval(() => {
        if (!isEditPlanningCoolingDown(oldService.priceCheckedDate as Date, activePeriod)) {
          clearInterval(interval)
          setEditPlanningDisabled(false)
        } else {
          setTimer(getRemaingSecondsBeforeEditing(oldService.priceCheckedDate as Date))
        }
      }, secondsToMilliseconds(1))
    }
  }, [oldService.priceCheckedDate])

  const handleCloseModal = (): void => {
    if (isEmpty(errors.services)) {
      onClose()
    } else {
      setTouched(setNestedObjectValues<FormikTouched<IEditServiceForm>>(errors, true))
    }
  }

  const planning = plannings[activePlanningIndex]

  if (!planning) return null

  const filterItinerary = (itinerary: IItinerary): boolean => {
    const isItineraryInPlanning = planning.itineraries.some(({ id }) => id === itinerary.id)
    return isItineraryInPlanning
  }

  let firstItineraryIndexFromActivePlanning = 0
  for (let i = 0; i < activePlanningIndex; i++) {
    firstItineraryIndexFromActivePlanning += plannings[i].itineraries.length
  }

  return (
    <ModalContainer large>
      <PlanningModalCloseButton type="submit" onClick={handleCloseModal}>
        <Heading.H5>
          <FormattedMessage id="dictionary.close" defaultMessage="Close" />
        </Heading.H5>
        <CancelIcon />
      </PlanningModalCloseButton>
      <PlanningModalContent>
        <FormLayout.Content isLargeContent>
          <Heading.H4>
            <FormattedMessage
              id="services.edit.planningModal.title"
              defaultMessage="Planning {planningIndex}"
              values={{ planningIndex: activePlanningIndex + 1 }}
            />
          </Heading.H4>
          {editPlanningDisabled && (
            <ItineraryNotificationContainer>
              <FormattedMessage
                id="services.form.section.itineraries.coolingDown.description"
                defaultMessage="You need to wait {timer} before editing this service again."
                values={{ timer: `${pad2(timer.minutes ?? 0)}:${pad2(timer.seconds ?? 0)}` }}
              />
            </ItineraryNotificationContainer>
          )}
          <FormLayout.Card small>
            <Row>
              <Column>
                <InputDate
                  intl={intl}
                  label={intl.formatMessage(messages.startDateLabel)}
                  value={planning.startDate}
                  onChange={(): void => undefined}
                  disabled
                />
              </Column>
              <Column>
                <InputDate
                  intl={intl}
                  label={intl.formatMessage(messages.endDateLabel)}
                  placeholder="--/--/----"
                  value={planning.endDate}
                  disabledDays={{ before: planning.startDate }}
                  onChange={(value): void => {
                    setFieldValue('endDate', value)
                  }}
                  disabled
                />
              </Column>
            </Row>
          </FormLayout.Card>
          <ItinerariesCard
            oldService={oldService}
            currentServiceIndex={currentServiceIndex}
            shownItineraries={values.services[currentServiceIndex].itineraries.filter(filterItinerary)}
            firstItineraryIndex={firstItineraryIndexFromActivePlanning}
            isShowingNotes
            onLastItineraryRemoved={onClose}
            disabled={editPlanningDisabled}
            isEndDateOptional={isEndDateOptional}
          />
          <VehiclesCard
            companyId={companyId}
            currentServiceIndex={currentServiceIndex}
            formDisabled={editPlanningDisabled}
          />
        </FormLayout.Content>
      </PlanningModalContent>
    </ModalContainer>
  )
}
